import Papa from 'papaparse';
import { io } from 'socket.io-client';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import React, { useCallback, Fragment, useState, useEffect } from 'react';

import {
    Button,
    Animation,
    DataTable,
    Autocomplete,
    SnackBarAlert,
    CircleLoading,
    TablePagination,
} from 'shared';
import {
    Box,
    List,
    Grid,
    Checkbox,
    ListItem,
    Typography,
    FormControl,
    ListItemText,
    CircularProgress,
    FormControlLabel,
} from '@mui/material';

import {
    startTour,
    TOUR_TYPES,
    completeTour,
    goToNextTour,
    updateTourStep,
    selectIsSkipped,
    selectTourStatus,
    selectIsCompleted,
    selectTutorialPreference,
} from 'store/slices/tour.slice';
import {
    isValidDate,
    statusOptions,
    sanitizePrice,
    extendedHeaders,
} from 'utils/fileValidationHelpers';
import {
    initialSteps,
    bankImportButtonStep,
    generateMappingSteps,
} from './utils/uploadStepsTutorials';
import {
    getTransactionFiles,
    getBankAccountsDropdown,
    importManualBankTransactions,
} from 'store/services/bank.Service';
import {
    uploadTransactionHeaders,
    requiredTransactionHeaders,
} from './utils/uploadTransactionHeaders';
import { Upload } from 'components/upload';
import { snackAlertData } from 'utils/constants';
import { handleErrorAlert } from 'utils/handleErrorAlert';
import { uploadTransactionsStyles } from './style/styles';
import { handleSuccessAlert } from 'utils/handleSuccessAlert';
import JoyrideTour from 'components/Onboarding/Tour/JoyRideTour';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL;
const TOKEN = process.env.REACT_APP_SOCKET_TOKEN;

const UploadTransactions = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const tourId = TOUR_TYPES.BANK_UPLOAD;
    const classes = uploadTransactionsStyles();
    const nextTourId = TOUR_TYPES.INVENTORY_REVIEW;
    const companyData = useSelector((state) => state.company.data);
    const isSkipped = useSelector((state) => selectIsSkipped(state, tourId));
    const tourStatus = useSelector((state) => selectTourStatus(state, tourId));
    const tutorialPreference = useSelector((state) => selectTutorialPreference(state));
    const isNextTourCompleted = useSelector((state) => selectIsCompleted(state, nextTourId));

    const [page, setPage] = useState(1);
    const [rows, setRows] = useState([]);
    const [data, setData] = useState([]);
    const [files, setFiles] = useState([]);
    const [steps, setSteps] = useState([]);
    const [headers, setHeaders] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [tourRun, setTourRun] = useState(false);
    const [loading, setLoading] = useState(false);
    const [fileName, setFileName] = useState(null);
    const [showStatus, setShowStatus] = useState(false);
    const [tourStage, setTourStage] = useState('upload');
    const [revertAmount, setRevertAmount] = useState(false);
    const [statusOption, setStatusOption] = useState('all');
    const [selectedValues, setSelectedValues] = useState({});
    const [importLoading, setImportLoading] = useState(false);
    const [alertData, setAlertData] = useState(snackAlertData);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [processingFiles, setProcessingFiles] = useState(null);

    const handleFileDrop = useCallback(
        (acceptedFiles, rejectedFiles) => {
            if (rejectedFiles && rejectedFiles.length > 0) {
                const hasFileTypeError = rejectedFiles.some((rejection) =>
                    rejection.errors.some((error) => error.code === 'file-invalid-type'),
                );

                if (hasFileTypeError) {
                    handleErrorAlert(
                        setAlertData,
                        'Invalid file type. Please upload a CSV file (.csv format only).',
                    );
                    return;
                }
            }
            if (!acceptedFiles || acceptedFiles.length === 0) {
                return;
            }

            const newFiles = acceptedFiles.map((file) =>
                Object.assign(file, {
                    preview: URL.createObjectURL(file),
                }),
            );
            const uploadedFile = newFiles[0];
            setFiles([uploadedFile]);
            if (newFiles) {
                setFileName(uploadedFile.name);
                Papa.parse(newFiles[0], {
                    header: true,
                    skipEmptyLines: true,
                    complete: (results) => {
                        if (results.data.length > 0) {
                            const firstRow = results.data[0];
                            const formatted = Object.keys(firstRow).map((header) => ({
                                id: header,
                                label: header,
                                sort: false,
                                verticalAlign: 'top',
                                changed: false,
                                labelForSelect: header,
                            }));
                            setHeaders(formatted);
                            setRows(results.data);
                            setData(results.data);
                            const manualRestart = tourStatus?.manuallyStarted;

                            if (tourStatus?.completed) {
                                setTourRun(false);
                                return;
                            }

                            if (
                                (tutorialPreference === 'skipped' || isSkipped) &&
                                manualRestart !== true
                            ) {
                                setTourRun(false);
                                return;
                            } else {
                                setTourRun(false);
                            }

                            dispatch(
                                updateTourStep({
                                    tourId,
                                    stepIndex: getTotalStepFromStage('mapping'),
                                }),
                            );
                            setTourStage('mapping');
                            setCurrentStepIndex(0);

                            setTimeout(() => {
                                setSteps(generateMappingSteps());
                                setTourRun(true);
                            }, 500);
                        }
                    },
                    error: (error) => {
                        console.error('Error parsing CSV:', error);
                    },
                });
            }
        },
        [files, dispatch, tourId],
    );

    const revertAmountSigns = (amountColumnId) => {
        if (!amountColumnId || !rows.length) return;

        const updatedRows = rows.map((row) => {
            const value = sanitizePrice(row[amountColumnId]);
            if (value === null) return row;

            return {
                ...row,
                [amountColumnId]: String(value * -1),
            };
        });

        setRows(updatedRows);
        setData(updatedRows);
    };

    const handleHeaderChange = (event, ch) => {
        const selectedValue = event.target.value;

        setHeaders((prevHeaders) =>
            prevHeaders.map((header) => {
                if (header.id === selectedValues[ch]) {
                    return {
                        ...header,
                        changed: false,
                        label: header.label.replace(` (${ch})`, ''),
                    };
                }
                if (header.id === selectedValue) {
                    return {
                        ...header,
                        changed: true,
                        label: `${header.label} (${ch})`,
                    };
                }
                return header;
            }),
        );

        setSelectedValues((prevSelectedValues) => {
            const updated = { ...prevSelectedValues, [ch]: selectedValue };
            if (ch === 'Amount *' && revertAmount && selectedValue) {
                revertAmountSigns(selectedValue);
            }
            return updated;
        });
    };

    const handleRevertToggle = (e) => {
        const checked = e.target.checked;
        setRevertAmount(checked);

        const amountCol = selectedValues['Amount *'];
        if (amountCol) {
            revertAmountSigns(amountCol);
        }
    };

    const handleClear = () => {
        setPage(1);
        setRows([]);
        setData([]);
        setFiles([]);
        setHeaders([]);
        setTourRun(true);
        setFileName(null);
        setShowStatus(false);
        setSelectedValues({});
        setStatusOption('all');
        setTourStage('upload');
        setCurrentStepIndex(0);
        setSteps(initialSteps);
        setRevertAmount(false);
        setSelectedAccount(null);
    };

    const formatData = () => {
        const camelCaseKey = (str) => {
            return str
                .replace(/\([^)]*\)/g, '') // Remove anything inside parentheses, including the parentheses
                .replace(/\*/g, '') // Remove asterisks
                .replace(/(?:^|\s)(\w)/g, (_, c) => (c ? c.toUpperCase() : '')) // Capitalize letters after spaces
                .replace(/\s+/g, '') // Remove spaces
                .replace(/^(\w)/, (_, c) => c.toLowerCase()); // Make the first letter lowercase
        };

        const updatedRows = [];

        const formattedRows = rows.map((row) => {
            const formattedRow = {};
            let status = { valid: true, issues: [] };

            Object.keys(selectedValues).forEach((key) => {
                const mappedKey = camelCaseKey(key);
                const mappedValue = selectedValues[key];
                let value = row[mappedValue];

                if (requiredTransactionHeaders.includes(key) && (!value || value === '')) {
                    status.valid = false;
                    status.issues.push(`${key} is required`);
                }

                if (key.toLowerCase().includes('date') && value) {
                    if (!isValidDate(value)) {
                        status.valid = false;
                        status.issues.push(`${key} is not a valid date`);
                    }
                }

                if (key.toLowerCase().includes('amount')) {
                    if (value) {
                        const number = sanitizePrice(value);
                        if (number === null) {
                            status.valid = false;
                            status.issues.push(`${key} is not a valid number`);
                        }
                        value = number;
                    }
                }

                formattedRow[mappedKey] = row[mappedValue] || null;
            });

            formattedRow._status = status;
            updatedRows.push({ ...row, _status: status });
            return formattedRow;
        });

        setRows(updatedRows);
        setData(updatedRows);
        return formattedRows;
    };

    const handleStatusChange = (e, status) => {
        setStatusOption(status.value);
        setPage(1);
        if (status.value === 'all') {
            setData(rows);
        } else if (status.value === 'valid') {
            setData(rows?.filter((row) => row?._status?.valid));
        } else if (status.value === 'invalid') {
            setData(rows?.filter((row) => !row?._status?.valid));
        }
    };

    const handleImport = async () => {
        try {
            const formattedData = formatData();

            const invalidRows = formattedData.filter((row) => !row._status.valid);

            if (invalidRows.length > 0) {
                handleErrorAlert(
                    setAlertData,
                    `${invalidRows.length} row(s) have errors. Please fix them before importing`,
                );
                setShowStatus(true);
                return;
            } else {
                setShowStatus(false);
            }

            setImportLoading(true);

            const response = await importManualBankTransactions({
                data: formattedData,
                companyId: companyData.id,
                bankAccountId: selectedAccount.value,
                fileName,
            });
            handleSuccessAlert(setAlertData, response.message);
            handleClear();
            handleGetTransactionFiles();

            if (tutorialPreference === 'skipped' || isSkipped) {
                dispatch(completeTour({ tourId, skipSaving: false }));
                return;
            }

            if (!isNextTourCompleted) {
                dispatch(
                    goToNextTour({
                        currentTourId: tourId,
                        nextTourId: TOUR_TYPES.INVENTORY_REVIEW,
                    }),
                );

                setTimeout(() => {
                    navigate('/purchases/for-review');
                }, 1500);
            } else {
                dispatch(completeTour({ tourId }));
            }
        } catch (error) {
            handleErrorAlert(setAlertData, 'Import failed.');
        } finally {
            setImportLoading(false);
        }
    };

    const handleGetAccounts = async () => {
        const response = await getBankAccountsDropdown(companyData?.id, 'manual', 'all');
        setAccounts(
            response?.data?.map((account) => ({
                label: account.name,
                value: account.id,
                total: account.transactions_count,
            })),
        );
    };

    const handleGetTransactionFiles = async () => {
        try {
            setLoading(true);
            const response = await getTransactionFiles(companyData?.id, 'processing');
            setProcessingFiles(response?.data);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleStepBefore = (idx) => {
        setCurrentStepIndex(idx);
    };

    const getTotalStepFromStage = (stage) => {
        switch (stage) {
            case 'upload':
                return 0;
            case 'mapping':
                return 1;
            case 'import':
                return 2;
            default:
                return 0;
        }
    };
    const handleTourFinish = () => {
        if (tourStage === 'upload') {
            dispatch(
                updateTourStep({
                    tourId,
                    stepIndex: getTotalStepFromStage('upload'),
                }),
            );
            return;
        } else if (tourStage === 'mapping') {
            dispatch(
                updateTourStep({
                    tourId,
                    stepIndex: getTotalStepFromStage('mapping'),
                }),
            );
            return;
        } else if (tourStage === 'import') {
            setTourRun(false);
            dispatch(completeTour({ tourId }));

            return;
        } else {
            setTourRun(false);
        }
    };

    const handleStepCallback = (data) => {
        const { action, index, type, status } = data;

        if (action === 'skip' || status === 'skipped' || data.type === 'tour:end') {
            setTourRun(false);
            dispatch(completeTour({ tourId }));
            return;
        }

        if (type === 'step:after' && action === 'next') {
            const globalStepIndex = getTotalStepFromStage(tourStage);
            dispatch(updateTourStep({ tourId, stepIndex: globalStepIndex }));

            if (tourStage === 'upload' && index === steps.length - 1) {
                setTourRun(false);
                setTourStage('mapping');

                setTimeout(() => {
                    setCurrentStepIndex(0);
                    setSteps(generateMappingSteps());
                    dispatch(
                        updateTourStep({ tourId, stepIndex: getTotalStepFromStage('mapping') }),
                    );
                    setTourRun(true);
                }, 1000);
            }

            if (tourStage === 'mapping' && index === steps.length - 1) {
                setTourRun(false);
                setTourStage('import');

                setTimeout(() => {
                    setCurrentStepIndex(0);
                    setSteps(bankImportButtonStep);
                    dispatch(
                        updateTourStep({ tourId, stepIndex: getTotalStepFromStage('import') }),
                    );
                    setTourRun(true);
                }, 1000);
            }
        }

        if (tourStage === 'import' && status === 'finished') {
            dispatch(completeTour({ tourId }));
            setTourRun(false);
        }
    };

    useEffect(() => {
        if (!companyData || processingFiles?.length === 0) return;

        const socket = io(SOCKET_URL, {
            transports: ['websocket'],
            path: '/api/socket/',
            query: {
                token: TOKEN,
            },
        });

        const eventName = `file/${companyData.id}/`;

        socket.off(eventName);
        socket.on(eventName, (data) => {
            handleSuccessAlert(setAlertData, `${data?.file_name} processed successfully`);

            setProcessingFiles((prev) => prev.filter((f) => f.id !== data.id));
        });

        socket.on('connect_error', (error) => {
            console.error('Connection error:', error);
        });

        return () => {
            socket.disconnect();
        };
    }, [companyData, processingFiles]);

    useEffect(() => {
        if (companyData) {
            handleGetTransactionFiles();
            handleGetAccounts();
        }
    }, [companyData]);

    useEffect(() => {
        const manualRestart = tourStatus?.manuallyStarted;
        if (
            (tutorialPreference === 'skipped' || isSkipped || tourStatus?.completed) &&
            manualRestart !== true
        ) {
            setTourRun(false);
            return;
        }

        setTimeout(() => {
            if (files.length === 0) {
                dispatch(startTour({ tourId }));
                setCurrentStepIndex(0);
                setSteps(initialSteps);
                setTourRun(true);
            } else if (tourStage === 'mapping') {
                setCurrentStepIndex(0);
                setSteps(generateMappingSteps());
                setTourRun(true);
            } else if (tourStage === 'import') {
                setCurrentStepIndex(0);
                setSteps(bankImportButtonStep);
                setTourRun(true);
            }
        }, 1000);
    }, [
        companyData,
        tourStatus?.completed,
        tourStatus?.manuallyStarted,
        tourStage,
        files.length,
        selectedAccount,
    ]);

    if (!companyData) {
        return <CircleLoading />;
    }

    return (
        <Fragment>
            {steps.length > 0 && (
                <JoyrideTour
                    key={`${tourStage}-${currentStepIndex}`}
                    steps={steps}
                    run={tourRun}
                    continuous={true}
                    spotlightClicks={true}
                    showSkipButton={true}
                    showProgress={true}
                    stepIndex={currentStepIndex}
                    onStepBefore={handleStepBefore}
                    onFinish={handleTourFinish}
                    isModal={false}
                    showBeacon={true}
                    callback={handleStepCallback}
                />
            )}
            <Grid container sx={classes.inventoryReconciliationWrapper}>
                {loading ? (
                    <CircleLoading />
                ) : (
                    <Fragment>
                        {processingFiles && processingFiles?.length > 0 && (
                            <Grid item sx={12} width="100%" marginBottom={2}>
                                <Box margin="auto" padding={2} borderRadius={2} boxShadow={3}>
                                    <Typography variant="h6" marginBottom={2}>
                                        Processing Files
                                    </Typography>
                                    <List>
                                        {processingFiles.map((file) => (
                                            <ListItem
                                                justifyContent="space-between"
                                                display="flex"
                                                key={file.id}
                                            >
                                                <ListItemText primary={file.file_name} />
                                                <CircularProgress size={24} />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>
                            </Grid>
                        )}
                        <Grid item xs={12} width="100%">
                            <Box id="upload-area-wrapper" data-tour="upload-area">
                                <Upload
                                    multiple
                                    accept={{
                                        '.csv': [],
                                    }}
                                    placeholder={'Drag and drop your files here (CSV)'}
                                    files={files}
                                    onDrop={handleFileDrop}
                                    onRemove={handleClear}
                                />
                            </Box>
                            {headers.length > 0 && (
                                <Box sx={classes.buttonsWrapper}>
                                    <Box data-tour="import-button">
                                        <Button
                                            text="Import"
                                            variant="outlined"
                                            disabled={
                                                requiredTransactionHeaders.some(
                                                    (req) => !selectedValues[req],
                                                ) || !selectedAccount
                                            }
                                            customStyles={classes.button}
                                            onClick={handleImport}
                                            loading={importLoading}
                                        />
                                    </Box>

                                    <Button
                                        text="Clear"
                                        onClick={handleClear}
                                        customStyles={classes.clearButton}
                                    />
                                </Box>
                            )}
                        </Grid>
                        {files.length > 0 && (
                            <Grid container marginTop={5}>
                                <Grid item sm={12} md={6} padding={2}>
                                    <Typography>Total Rows: {rows.length}</Typography>
                                    {showStatus && (
                                        <Typography marginBottom="20px" color="error">
                                            Total Rows with error:{' '}
                                            {rows?.filter((row) => !row?._status?.valid).length}
                                        </Typography>
                                    )}
                                    <Box data-tour="account-selection">
                                        <Autocomplete
                                            options={accounts}
                                            labelKey="label"
                                            valueKey="value"
                                            placeholder="Select account"
                                            value={selectedAccount}
                                            onChange={(e, value) => {
                                                setSelectedAccount(value);
                                            }}
                                            showTotal={true}
                                        />
                                    </Box>
                                </Grid>
                                {headers.length > 0 && (
                                    <Grid item sm={12} md={6}>
                                        {uploadTransactionHeaders.map((ch, i) => (
                                            <Fragment>
                                                <Box
                                                    key={i}
                                                    sx={classes.dropdownsWrapper}
                                                    data-tour={`map-${ch
                                                        .toLowerCase()
                                                        .replace(/[^a-z0-9]+/g, '-')
                                                        .replace(/(^-|-$)/g, '')}`}
                                                >
                                                    <Typography sx={classes.flex}>{ch}</Typography>
                                                    <FormControl sx={classes.flex}>
                                                        <Autocomplete
                                                            options={headers.filter(
                                                                (h) =>
                                                                    !h.changed ||
                                                                    selectedValues[ch] === h.id,
                                                            )}
                                                            labelKey="labelForSelect"
                                                            valueKey="id"
                                                            placeholder="Select Column"
                                                            value={
                                                                headers.find(
                                                                    (h) =>
                                                                        h.id === selectedValues[ch],
                                                                ) || null
                                                            }
                                                            onChange={(e, value) => {
                                                                const event = {
                                                                    target: {
                                                                        value: value
                                                                            ? value.id
                                                                            : '',
                                                                    },
                                                                };
                                                                handleHeaderChange(event, ch);
                                                            }}
                                                            disableClearable={false}
                                                        />
                                                    </FormControl>
                                                </Box>
                                                {ch === 'Amount *' && (
                                                    <Box>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={revertAmount}
                                                                    onChange={handleRevertToggle}
                                                                    color="primary"
                                                                />
                                                            }
                                                            label="Revert Amount"
                                                        />
                                                    </Box>
                                                )}
                                            </Fragment>
                                        ))}
                                    </Grid>
                                )}
                            </Grid>
                        )}
                    </Fragment>
                )}
            </Grid>

            {headers.length > 0 && (
                <Grid container sx={classes.inventoryReconciliationWrapper} marginTop="10px">
                    {showStatus && (
                        <Grid item xs={6}>
                            <Autocomplete
                                options={statusOptions}
                                labelKey="label"
                                valueKey="value"
                                inlineLabel="Status"
                                placeholder="Select by Status"
                                value={statusOptions.find((type) => type.value == statusOption)}
                                onChange={handleStatusChange}
                                disableClearable
                            />
                        </Grid>
                    )}
                    <Grid item xs={12} marginTop="10px">
                        <DataTable
                            headCells={extendedHeaders(headers, showStatus)}
                            data={data.slice((page - 1) * 10, page * 10)}
                        ></DataTable>
                    </Grid>
                    <Grid item xs={12} marginTop="10px">
                        <TablePagination
                            page={page}
                            count={data.length}
                            onChange={(e, pageNumber) => setPage(pageNumber)}
                        />
                    </Grid>
                </Grid>
            )}

            {importLoading && <Animation title="Uploading your bank activities" />}

            <SnackBarAlert alertData={alertData} setAlertData={setAlertData} />
        </Fragment>
    );
};

export default UploadTransactions;
